import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  User, ArrowLeft, Loader, AlertCircle, Save, School
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Professor Edit Page
 * Allows admins to edit professor details
 */
const ProfessorEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [professor, setProfessor] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [bio, setBio] = useState('');
  const [schoolId, setSchoolId] = useState('');
  
  useEffect(() => {
    // Redirect if user is not admin or moderator
    if (user && user.role !== 'admin' && user.role !== 'moderator') {
      toast.error('You do not have permission to access this page');
      navigate('/professors');
      return;
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch professor data
        const professorRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/professors/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!professorRes.ok) {
          throw new Error('Failed to fetch professor details');
        }
        
        const professorData = await professorRes.json();
        setProfessor(professorData);
        
        // Set initial form values
        setName(professorData.name || '');
        setEmail(professorData.email || '');
        setBio(professorData.bio || '');
        setSchoolId(professorData.schoolId || '');
        
        // Fetch schools for dropdown (only admin needs this)
        if (user?.role === 'admin') {
          const schoolsRes = await fetch(
            `${process.env.REACT_APP_API_URL}/api/schools`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          if (!schoolsRes.ok) {
            throw new Error('Failed to fetch schools');
          }
          
          const schoolsData = await schoolsRes.json();
          setSchools(schoolsData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        toast.error('Failed to load professor data');
      } finally {
        setLoading(false);
      }
    };
    
    if (id && user) {
      fetchData();
    }
  }, [id, user, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name) {
      toast.error('Name is required');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        name,
        email: email || null,
        bio: bio || null
      };
      
      // Only include schoolId if user is admin
      if (user?.role === 'admin') {
        payload.schoolId = schoolId;
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/professors/${id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update professor');
      }
      
      toast.success('Professor updated successfully');
      
      // Navigate back to the professor details page
      navigate(`/professors/${id}`);
    } catch (err) {
      console.error('Error updating professor:', err);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading professor data...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading Professor
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Go Back
        </button>
      </div>
    );
  }
  
  if (!professor) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Professor Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          The professor you're trying to edit couldn't be found.
        </p>
        <Link
          to="/admin/professors"
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Back to Professors
        </Link>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Navigation breadcrumbs */}
      <div className="flex items-center mb-6 text-sm">
        <Link to="/admin" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Dashboard
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link to="/admin/professors" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Professors
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          Edit Professor
        </span>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mr-4">
            <User size={24} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Edit Professor: {professor.name}
          </h1>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                required
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            
            {user?.role === 'admin' && (
              <div>
                <label htmlFor="school" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  School *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <School className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="school"
                    value={schoolId}
                    onChange={(e) => setSchoolId(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    required
                  >
                    <option value="">Select School</option>
                    {schools.map((school) => (
                      <option key={school.id} value={school.id}>
                        {school.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
          
          <div>
            <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Bio
            </label>
            <textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows="4"
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                saving ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              <Save className="mr-2 -ml-1 h-5 w-5" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfessorEditPage;